/**
 * @file intercom.js
 * @description Central utility for all Intercom API interactions.
 *
 * ARCHITECTURE RULE:
 *   All Intercom operations go through this module.
 *   Do NOT import from '@intercom/messenger-js-sdk' anywhere else in the codebase.
 *
 * SDK LOADING:
 *   The @intercom/messenger-js-sdk package handles script injection automatically.
 *   `Intercom()` (default export) must be called first — it loads the SDK and
 *   sets up window.Intercom. All other named exports (boot, shutdown, update…)
 *   only work after this first call. The App ID comes from the API at runtime
 *   (chatProvider.INTERCOM_APP_ID), supporting multi-tenant deployments.
 *
 * BOOT FLOW (Intercom recommended SPA pattern):
 *   1. Page load (guest)  → bootAnonymous()  → IntercomInit() [loads SDK + boots anonymous]
 *   2. User logs in       → bootAsUser()     → update({ user_id, user_hash, ... })
 *                           Upgrades the anonymous session in place — NO shutdown().
 *                           Preserves conversation history. Calling shutdown() here would
 *                           destroy the session and cause "Content could not be loaded".
 *   3. User logs out      → bootAnonymous()  → shutdown() + boot({ app_id only })
 *                           shutdown() is ONLY correct here — it clears the user identity.
 *
 * VISIBILITY STRATEGY (dual-layer):
 *   1. CSS  — `body.hide-chat-mobile` managed by ZohoChatButtonControl
 *   2. JS   — `hideLauncher()` / `showLauncher()` via update()
 */

import IntercomInit, {
  boot,
  shutdown,
  update,
  show
} from '@intercom/messenger-js-sdk'
import { mapToIntercomLocale } from 'utils/chatLocale'

// ─────────────────────────────────────────────────────────────────────────────
// Guard
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Returns true when the Intercom SDK is loaded and callable.
 */
export const isIntercomReady = () => typeof window.Intercom === 'function'

// ─────────────────────────────────────────────────────────────────────────────
// Launcher visibility
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Hide the default floating launcher bubble.
 * Pair with `document.body.classList.add('hide-chat-mobile')` for instant effect.
 */
export const hideLauncher = () => {
  if (isIntercomReady()) {
    update({ hide_default_launcher: true })
  }
}

/**
 * Show the default floating launcher bubble.
 * Pair with `document.body.classList.remove('hide-chat-mobile')` for instant effect.
 */
export const showLauncher = () => {
  if (isIntercomReady()) {
    update({ hide_default_launcher: false })
  }
}

// ─────────────────────────────────────────────────────────────────────────────
// Chat window
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Open the Intercom messenger.
 *
 * @param {string} [languageCode] - BCP-47 language code (e.g. 'en', 'de').
 */
export const openChat = (languageCode) => {
  if (!isIntercomReady()) return
  if (languageCode) {
    update({ language_override: mapToIntercomLocale(languageCode) })
  }
  show()
}

// ─────────────────────────────────────────────────────────────────────────────
// Session management
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Boot Intercom for an authenticated user.
 *
 * Uses Intercom's recommended SPA pattern:
 *   - If the SDK is already loaded (normal guest→login flow): call update() with full user
 *     identity. This upgrades the anonymous session in place without destroying it.
 *     shutdown() is NOT called here — doing so would wipe conversation history and
 *     cause "Content could not be loaded" when clicking old conversations.
 *   - If the SDK is not yet loaded (e.g., hard refresh while logged in): use IntercomInit
 *     with full user data to boot directly as an authenticated user in one step.
 *
 * @param {Object} params
 * @param {string} params.appId      - Intercom App ID (from API — chatProvider.INTERCOM_APP_ID)
 * @param {string} params.userId     - Intercom user_id `<tenant>_<userId>` (see getIntercomUserId in utils/constants)
 * @param {string} params.name       - User full name
 * @param {string} params.email      - User email address
 * @param {string} params.createdAt  - ISO timestamp of account creation
 * @param {string} params.userHash   - HMAC-SHA256 of userId, signed by the backend (get-user-details `intercomUserHash`)
 * @param {string[]} [params.tags]   - Optional array of user tags
 */
export const bootAsUser = ({ appId, userId, name, email, createdAt, userHash, tags, languageCode }) => {
  if (!appId) return

  const userPayload = {
    app_id: appId,
    alignment: 'right',
    user_id: userId,
    name,
    email,
    created_at: createdAt,
    user_hash: userHash,
    tags: tags?.length ? tags.toString() : null,
    language_override: mapToIntercomLocale(languageCode)
  }

  if (!isIntercomReady()) {
    // SDK not loaded yet (e.g. hard refresh while logged in — bootAnonymous never ran).
    // Boot directly as an authenticated user in one step.
    IntercomInit(userPayload)
  } else {
    // SDK already loaded (anonymous session active from bootAnonymous on page load).
    // update() upgrades the session in place — Intercom's recommended login pattern.
    // DO NOT call shutdown() here: it destroys the session and breaks conversation history.
    update(userPayload)
  }
}

/**
 * Boot Intercom anonymously (visitor not logged in).
 *
 * On the very first page load, `IntercomInit()` loads the SDK and boots
 * anonymously via window.intercomSettings. On subsequent calls (e.g. after
 * logout), it calls `shutdown()` + `boot()` to clear the previous session.
 *
 * @param {Object} params
 * @param {string} params.appId - Intercom App ID (from API — chatProvider.INTERCOM_APP_ID)
 * @param {string} [params.languageCode] - BCP-47 language code
 */
/**
 * Clears all Intercom session cookies for the given appId.
 *
 * Recommended by Intercom (Option A) for the logged-out / fresh-load path.
 * Intercom sets cookies under the bare hostname AND the dotted apex domain,
 * so we expire all variants to guarantee nothing is left to restore.
 *
 * Cookie names used by Intercom:
 *   intercom-id-{appId}         — visitor/user identity
 *   intercom-session-{appId}    — session token
 *   intercom-device-id-{appId}  — device fingerprint
 *   intercom-id                 — legacy / fallback
 *
 * @param {string} appId - Intercom App ID
 */
const clearIntercomCookies = (appId) => {
  const names = [
    `intercom-id-${appId}`,
    `intercom-session-${appId}`,
    `intercom-device-id-${appId}`,
    'intercom-id',
  ]

  const host = window?.location?.hostname || ''
  // Compute apex/parent domain (e.g. 'www.example.com' → 'example.com').
  // Intercom sets cookies on the dotted apex domain (e.g. '.example.com'),
  // so we must target that variant or the delete will not take effect.
  const parts = host.split('.')
  const apex = parts.length > 2 ? parts.slice(-2).join('.') : host

  // Expire every plausible domain variant: no domain, bare host, dotted host,
  // bare apex, dotted apex. Covers localhost, subdomains, and CDN edge cases.
  const domains = [
    '',
    `; domain=${host}`,
    `; domain=.${host}`,
    `; domain=${apex}`,
    `; domain=.${apex}`,
  ]

  names.forEach((name) =>
    domains.forEach((domain) => {
      document.cookie = `${name}=; expires=Thu, 01 Jan 1970 00:00:00 GMT; path=/${domain};`
    })
  )
}

export const bootAnonymous = ({ appId, languageCode }) => {
  if (!appId) return

  const bootOptions = { app_id: appId, alignment: 'right' }
  if (languageCode) {
    bootOptions.language_override = mapToIntercomLocale(languageCode)
  }

  if (!isIntercomReady()) {
    // SDK not yet loaded — clear session cookies FIRST (Intercom's Option A) so
    // there is nothing left to restore when the SDK boots. Then IntercomInit +
    // boot produces a truly anonymous session without needing shutdown().
    clearIntercomCookies(appId)
    IntercomInit(bootOptions)
    boot(bootOptions)
  } else {
    // SDK already loaded — use the standard shutdown() + boot() to clear the
    // current session and start a fresh anonymous one.
    shutdown()
    boot(bootOptions)
  }
}

/**
 * Update the Intercom language override dynamically.
 *
 * @param {string} languageCode - BCP-47 language code (e.g. 'en', 'de').
 */
export const updateLanguage = (languageCode) => {
  if (isIntercomReady() && languageCode) {
    update({ language_override: mapToIntercomLocale(languageCode) })
  }
}
